import type { ShortcutScope } from '@/shared/config/shortcuts';
import type { KeyCombo } from '@/shared/lib/keyCombo';
import { create } from 'zustand';

/** A shortcut handler. Receives the originating keydown so it can inspect/consume it. */
export type ShortcutHandler = (event: KeyboardEvent) => void;

/**
 * An active binding as the host consumes it: the handler plus the keybinding
 * resolved from the central config at registration time (so the host reads only
 * the store, never the config table), plus a monotonic `seq` used to break ties
 * toward the most-recently-registered binding.
 */
export interface ResolvedShortcut {
  keys: KeyCombo;
  scope: ShortcutScope;
  handler: ShortcutHandler;
  preventDefault?: boolean;
  allowInInput?: boolean;
  seq: number;
}

/**
 * Registry-driven shortcut state. Lives in a Zustand store (per the "stores live
 * in a `stores/` segment" rule), mirroring the dialog surface: the imperative
 * `registerShortcut`/`unregisterShortcut` façade (`src/shared/lib/shortcutRegistry.ts`)
 * drives it and the single `<ShortcutHost/>` reads the active bindings at
 * dispatch time. Keyed by command id (a `Record`) so re-registering an id
 * replaces its binding and `unregister` is a delete.
 */
interface ShortcutState {
  shortcuts: Record<string, ResolvedShortcut>;
  register: (id: string, binding: Omit<ResolvedShortcut, 'seq'>) => void;
  unregister: (id: string) => void;
}

let seqCounter = 0;

export const useShortcutStore = create<ShortcutState>((set) => ({
  shortcuts: {},
  register: (id, binding) => {
    seqCounter += 1;
    const resolved: ResolvedShortcut = { ...binding, seq: seqCounter };
    set((s) => ({ shortcuts: { ...s.shortcuts, [id]: resolved } }));
  },
  unregister: (id) =>
    set((s) => {
      if (!(id in s.shortcuts)) return s;
      const next = { ...s.shortcuts };
      delete next[id];
      return { shortcuts: next };
    }),
}));
